"use client";

import React from "react";
import { ButtonFactoryProps } from "./types";

/**
 * Default icon set for navigation buttons
 * These SVG icons can be overridden by providing custom icons in the action configuration
 */
const defaultIcons = {
  back: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  menu: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M3 12h18M3 6h18M3 18h18" />
    </svg>
  ),
  close: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
};

/**
 * Button Factory Function
 *
 * Creates navigation buttons based on action configuration and theme.
 * Handles icon selection, styling, and event handling for nav header buttons.
 *
 * @param action - Button action configuration including type, icon, onClick handler
 * @param theme - Theme configuration for button styling
 * @returns React button element or null if no valid action provided
 */
export const createButton = ({
  action,
  theme,
}: ButtonFactoryProps): React.ReactNode => {
  if (!action) return null;

  const { type, icon, onClick, ariaLabel, className = "" } = action;

  // Determine the icon to use
  let buttonIcon = icon;
  if (!buttonIcon && type && type !== "custom") {
    buttonIcon = defaultIcons[type];
  }

  // If still no icon and type is custom, return null
  if (!buttonIcon) return null;

  // Use theme button class or default
  const buttonClass = theme?.button || "p-2";

  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel || type || "button"}
      className={`flex items-center justify-center transition-colors ${buttonClass} ${className}`.trim()}
    >
      {buttonIcon}
    </button>
  );
};
